import { useFormik } from "formik";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Link, useHistory, withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useCreateStaffUser } from "../../../hooks/useUsersList";
import { getStoredAuthUser } from "../../../utils/authUser";

/**
 * Internal staff account creation (assistant, committee member, admin).
 *
 * `POST /users/` cannot do this: it is public and hardcodes the USER role, so
 * until now the only non-USER account was the one created by the bootstrap
 * script.
 */
const STAFF_ROLES = [
  {
    value: "ASSISTANT",
    label: "Assistant",
    description:
      "Peut rédiger et valider des articles, gérer publicités, matériaux et innovations.",
  },
  {
    value: "COMMITTEE",
    label: "Membre du comité",
    description:
      "Peut valider les articles, les experts et les produits mis en avant.",
  },
  {
    value: "ADMIN",
    label: "Administrateur",
    description:
      "Accès complet à la gestion de la plateforme. Réservé aux super administrateurs.",
    superAdminOnly: true,
  },
];

const AddStaffMember = (props) => {
  document.title = props.t("Créer un compte interne | Le Conseiller du BTP");

  const history = useHistory();
  const createStaff = useCreateStaffUser();
  const [showPassword, setShowPassword] = useState(false);

  // Only a super admin may mint another administrator; the backend enforces
  // the same rule, this just avoids offering an option that would be refused.
  const isSuperAdmin = useMemo(() => {
    const authUser = getStoredAuthUser();
    const roles = Array.isArray(authUser?.role) ? authUser.role : [];
    return roles.includes("super_admin");
  }, []);

  const availableRoles = useMemo(
    () => STAFF_ROLES.filter((role) => !role.superAdminOnly || isSuperAdmin),
    [isSuperAdmin]
  );

  const validation = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      username: "",
      email: "",
      phone_number: "",
      password: "",
      role_code: "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required(props.t("Le champ est requis")),
      last_name: Yup.string().required(props.t("Le champ est requis")),
      username: Yup.string()
        .min(3, props.t("3 caractères minimum"))
        .required(props.t("Le champ est requis")),
      email: Yup.string()
        .email(props.t("Adresse e-mail invalide"))
        .required(props.t("Le champ est requis")),
      phone_number: Yup.string()
        .min(6, props.t("Numéro trop court"))
        .notRequired(),
      password: Yup.string()
        .min(8, props.t("8 caractères minimum"))
        .matches(/[a-z]/, props.t("Doit contenir une minuscule"))
        .matches(/[A-Z]/, props.t("Doit contenir une majuscule"))
        .matches(/\d/, props.t("Doit contenir un chiffre"))
        .required(props.t("Le champ est requis")),
      role_code: Yup.string().required(props.t("Choisissez un rôle")),
    }),
    onSubmit: async (values) => {
      const payload = {
        first_name: values.first_name,
        last_name: values.last_name,
        username: values.username,
        email: values.email,
        password: values.password,
        role_code: values.role_code,
      };
      if (values.phone_number.trim()) {
        payload.phone_number = values.phone_number.trim();
      }

      try {
        await createStaff.mutateAsync(payload);
        validation.resetForm();
        history.push("/pages-team");
      } catch (_) {
        // handled by toast in hook
      }
    },
  });

  const field = (name, label, type = "text", required = true) => (
    <div className="mb-3">
      <Label className="form-label" htmlFor={name}>
        {label} {required ? <span style={{ color: "red" }}>*</span> : null}
      </Label>
      <Input
        id={name}
        name={name}
        type={type}
        className="form-control"
        value={validation.values[name]}
        onChange={validation.handleChange}
        onBlur={validation.handleBlur}
        invalid={Boolean(validation.touched[name] && validation.errors[name])}
      />
      {validation.touched[name] && validation.errors[name] ? (
        <FormFeedback type="invalid">{validation.errors[name]}</FormFeedback>
      ) : null}
    </div>
  );

  const selectedRole = STAFF_ROLES.find(
    (role) => role.value === validation.values.role_code
  );

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />
      <Container fluid>
        <BreadCrumb
          title={props.t("Créer un compte interne")}
          pageTitle={props.t("Utilisateurs")}
        />

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="alert alert-info" role="alert">
                    <i className="ri-information-line align-bottom me-1"></i>
                    {props.t(
                      "Le collaborateur recevra un e-mail lui indiquant son adresse de connexion. Communiquez-lui son mot de passe initial par un autre canal : il pourra le modifier depuis sa page profil."
                    )}
                  </div>

                  <Row>
                    <Col lg={6}>
                      {field("last_name", props.t("Nom"))}
                    </Col>
                    <Col lg={6}>
                      {field("first_name", props.t("Prénom(s)"))}
                    </Col>
                    <Col lg={6}>
                      {field("username", props.t("Nom d'utilisateur"))}
                    </Col>
                    <Col lg={6}>
                      {field("email", props.t("Adresse e-mail"), "email")}
                    </Col>
                    <Col lg={6}>
                      {field(
                        "phone_number",
                        props.t("Téléphone"),
                        "text",
                        false
                      )}
                    </Col>

                    <Col lg={6}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="password">
                          {props.t("Mot de passe initial")}{" "}
                          <span style={{ color: "red" }}>*</span>
                        </Label>
                        <div className="position-relative">
                          <Input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            className="form-control pe-5"
                            autoComplete="new-password"
                            value={validation.values.password}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={Boolean(
                              validation.touched.password &&
                                validation.errors.password
                            )}
                          />
                          <button
                            type="button"
                            className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                            onClick={() => setShowPassword((v) => !v)}
                            tabIndex={-1}
                          >
                            <i
                              className={
                                showPassword ? "ri-eye-off-fill" : "ri-eye-fill"
                              }
                            ></i>
                          </button>
                          {validation.touched.password &&
                          validation.errors.password ? (
                            <FormFeedback type="invalid">
                              {validation.errors.password}
                            </FormFeedback>
                          ) : null}
                        </div>
                        <small className="text-muted">
                          {props.t(
                            "8 caractères minimum, avec majuscule, minuscule et chiffre."
                          )}
                        </small>
                      </div>
                    </Col>

                    <Col lg={12}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="role_code">
                          {props.t("Rôle")}{" "}
                          <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                          id="role_code"
                          name="role_code"
                          type="select"
                          className="form-select"
                          value={validation.values.role_code}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={Boolean(
                            validation.touched.role_code &&
                              validation.errors.role_code
                          )}
                        >
                          <option value="">
                            {props.t("Sélectionner un rôle")}
                          </option>
                          {availableRoles.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </Input>
                        {validation.touched.role_code &&
                        validation.errors.role_code ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.role_code}
                          </FormFeedback>
                        ) : null}
                        {selectedRole ? (
                          <small className="text-muted">
                            {selectedRole.description}
                          </small>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <div className="text-end mb-3">
                <Link to="/pages-team" className="btn btn-primary me-2">
                  {props.t("Retour")}
                </Link>
                <button
                  type="submit"
                  className="btn btn-warning w-sm m-2"
                  disabled={createStaff.isPending}
                >
                  {createStaff.isPending && (
                    <Spinner size="sm" className="me-2" />
                  )}
                  {props.t("Créer le compte")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

AddStaffMember.propTypes = {
  t: PropTypes.any,
};

export default withRouter(withTranslation()(AddStaffMember));
